import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { connect } from "react-redux";
import { fetchSavedSearchBySlug } from "../../actions/savedSearch";
import MapResults from "./MapResults";
import ResultSideBody from "./restaurantResults/ResultSideBody";
import Breadcrumb from "../common/Breadcrumb";
import ViewToggle from "../common/ViewToggle";
import ListOverviewPanel from "./lists/ListOverviewPanel";
import { citySlug } from "../../util/locationSlug";
import { stateAbbrev } from "../../util/stateAbbrev";

const buildMapSearchParams = (savedSearch, existingView) => {
  const params = new URLSearchParams();
  if (savedSearch.searchParams?.lat != null) {
    params.set("lat", savedSearch.searchParams.lat.toString());
  }
  if (savedSearch.searchParams?.lng != null) {
    params.set("lng", savedSearch.searchParams.lng.toString());
  }
  if (savedSearch.searchParams?.keywords) {
    params.set("keywords", savedSearch.searchParams.keywords);
  }
  if (savedSearch.searchParams?.cuisine?.length > 0) {
    params.set("cuisine", savedSearch.searchParams.cuisine.join(","));
  }
  if (savedSearch.searchParams?.radius) {
    params.set("radius", savedSearch.searchParams.radius.toString());
  }
  if (savedSearch.searchParams?.zoom) {
    params.set("zoom", savedSearch.searchParams.zoom.toString());
  }
  if (savedSearch.searchParams?.minLeafCount) {
    params.set("minLeaf", savedSearch.searchParams.minLeafCount.toString());
  }
  if (savedSearch.searchParams?.verificationTier) {
    params.set("tier", savedSearch.searchParams.verificationTier);
  }
  if (savedSearch.curatedRestaurantIds?.length > 0) {
    params.set("include", savedSearch.curatedRestaurantIds.join(","));
  }
  params.set("listName", savedSearch.name);
  if (!savedSearch.searchParams?.lat && savedSearch.geoJson?.coordinates) {
    params.set("lng", savedSearch.geoJson.coordinates[0].toString());
    params.set("lat", savedSearch.geoJson.coordinates[1].toString());
  }
  if (existingView) params.set("view", existingView);
  return params;
};

const ListPage = ({ token }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") === "overview" ? "overview" : "map";
  const [savedSearch, setSavedSearch] = useState(
    window.__SSR_DATA__?.savedSearch || null
  );
  const [loading, setLoading] = useState(!savedSearch);
  const [error, setError] = useState(null);

  useEffect(function consumeSsrDataOnMount() {
    // Consume SSR data once
    if (window.__SSR_DATA__) {
      delete window.__SSR_DATA__;
    }
  }, []);

  useEffect(function loadSavedSearchBySlug() {
    if (savedSearch) return; // Already have data (from SSR)

    const load = async () => {
      try {
        const data = await fetchSavedSearchBySlug(slug, token);
        setSavedSearch(data);
        const currentView = new URLSearchParams(window.location.search).get("view");
        const params = buildMapSearchParams(data, currentView);
        navigate(`?${params.toString()}`, { replace: true });
      } catch (err) {
        setError(
          err.response?.status === 404
            ? "List not found"
            : "Failed to load list"
        );
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  // Also set URL params if we got data from SSR (and current URL only has view=)
  useEffect(function syncUrlParamsFromSsrData() {
    if (!savedSearch) return;
    const current = new URLSearchParams(location.search);
    const hasMapParams =
      current.has("lat") || current.has("lng") || current.has("listName");
    if (hasMapParams) return;
    const params = buildMapSearchParams(savedSearch, current.get("view"));
    navigate(`?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSearch]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <i className="fas fa-spinner fa-spin fa-2x" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <i className="fas fa-exclamation-circle fa-2x" style={{ color: "#c62828" }} />
        <p>{error}</p>
      </div>
    );
  }

  const displayCity = savedSearch.state
    ? `${savedSearch.cityName}, ${savedSearch.state}`
    : savedSearch.cityName;

  const stripLinkSyntax = (text) =>
    text ? text.replace(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g, "$1") : text;

  const seoTitle =
    savedSearch.seo?.title || `${savedSearch.name} | VegifyIt`;
  const seoDescription = stripLinkSyntax(
    savedSearch.seo?.description ||
    savedSearch.seoMetaDescription ||
    savedSearch.description ||
    `Discover vegan-friendly dishes ${displayCity ? `near ${displayCity}` : ""}. ${savedSearch.name} on VegifyIt.`
  );

  const crumbs = [{ label: "Explore", href: "/explore" }];
  if (savedSearch.cityName && savedSearch.state) {
    crumbs.push({
      label: `${savedSearch.cityName}, ${stateAbbrev(savedSearch.state)}`,
      href: `/cities/${citySlug(savedSearch.state)}/${citySlug(savedSearch.cityName)}`,
    });
  }
  crumbs.push({ label: savedSearch.name });

  // Embed mode (/lists/:slug/embed) hides chrome — skip the breadcrumb too.
  const isEmbed = location.pathname.endsWith("/embed");
  const overviewHidden = view !== "overview";
  const mapHidden = view !== "map";

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta
          property="og:url"
          content={`${window.location.origin}/lists/${slug}`}
        />
        <meta property="og:type" content="website" />
      </Helmet>
      {!isEmbed && (
        <Breadcrumb items={crumbs} rightSlot={<ViewToggle />} />
      )}
      {!isEmbed && (
        <div
          style={
            overviewHidden ? { display: "none" } : { flex: "1 1 auto" }
          }
        >
          <ListOverviewPanel
            savedSearch={savedSearch}
            displayCity={displayCity}
          />
        </div>
      )}
      <div
        className={
          !isEmbed && mapHidden
            ? "main-app-stash"
            : `main-app${!isEmbed ? " main-app--with-breadcrumb" : ""}`
        }
        style={!isEmbed && mapHidden ? { display: "none" } : undefined}
      >
        <MapResults isMarkerShown />
        <ResultSideBody />
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(ListPage);
